/* Az Bilinenler: the list of facts (filtered by topic through ?konu=) and one page per fact, rendered with
   the lesson blocks. Unknown fact ids fall back to the list. */
import { useSearchParams } from 'react-router';
import { useGo } from '../../app/navigation';
import { useSubPath } from '../../app/useSubPath';
import { FACTS, FACTS_ROUTE, type Fact, type FactTopic, factById, factRoute, TOPIC_NAME } from '../../content/facts';
import { Icon } from '../../ui/icons';
import { Rich } from '../../ui/Rich';
import { modStyle, vars } from '../../ui/style';
import { Tabs } from '../../ui/widgets';
import { LessonBlocks } from '../lesson/blocks';

type Filter = 'hepsi' | FactTopic;

const count = (t: FactTopic) => String(FACTS.filter((f) => f.topic === t).length);

/** Card linking to a fact; also used on the home page. */
export function FactCard({ fact }: { fact: Fact }) {
  const go = useGo();
  return (
    <button
      type="button"
      className="fact-card"
      style={modStyle(fact.topic)}
      data-topic={fact.topic}
      onClick={() => go(factRoute(fact))}
    >
      <span className="fact-chip">
        <Icon name={fact.topic === 'gitar' ? 'pick' : 'voice'} />
        {TOPIC_NAME[fact.topic]}
      </span>
      <span className="fact-title">
        <Rich text={fact.title} inert />
      </span>
      <span className="fact-teaser">
        <Rich text={fact.teaser} inert />
      </span>
      <span className="fact-meta">
        <span>{fact.minutes} dk</span>
        <Icon name="right" />
      </span>
    </button>
  );
}

function FactList() {
  const [params, setParams] = useSearchParams();
  const konu = params.get('konu');
  const filter: Filter = konu === 'gitar' || konu === 'vokal' ? konu : 'hepsi';
  const shown = filter === 'hepsi' ? FACTS : FACTS.filter((f) => f.topic === filter);
  return (
    <div className="facts-page" style={vars({ '--mod': 'var(--accent)' })}>
      <header className="mod-head">
        <div className="mod-icon">
          <Icon name="bulb" />
        </div>
        <div className="mod-titles">
          <h1>Az Bilinenler</h1>
          <p>
            Gitarda ve şarkı söylerken çoğu kişinin ezberleyip nedenini hiç düşünmediği bilgiler. Her birinin mantığını
            anlatıyor, dinletiyor ve gösteriyoruz.
          </p>
        </div>
      </header>
      <Tabs<Filter>
        items={[
          { id: 'hepsi', label: 'Hepsi', badge: String(FACTS.length) },
          { id: 'gitar', label: 'Gitar', icon: 'pick', badge: count('gitar') },
          { id: 'vokal', label: 'Vokal', icon: 'voice', badge: count('vokal') },
        ]}
        active={filter}
        onChange={(f) => setParams(f === 'hepsi' ? {} : { konu: f }, { replace: true })}
      />
      <div className="facts-grid">
        {shown.map((f) => (
          <FactCard key={f.id} fact={f} />
        ))}
      </div>
      <p className="facts-note">Her bilgi en az iki güvenilir kaynakla karşılaştırılarak hazırlandı.</p>
    </div>
  );
}

function FactPage({ fact }: { fact: Fact }) {
  const go = useGo();
  const next = FACTS[(FACTS.indexOf(fact) + 1) % FACTS.length] as Fact;
  return (
    <article className="lesson fact" style={modStyle(fact.topic)}>
      <header className="lesson-head">
        <button type="button" className="link-back" onClick={() => go(FACTS_ROUTE)}>
          <Icon name="left" />
          <span>Az Bilinenler</span>
        </button>
        <div className="eyebrow">
          {TOPIC_NAME[fact.topic]} · {fact.minutes} dk
        </div>
        <h1 className="lesson-title">
          <Rich text={fact.title} />
        </h1>
        <p className="lesson-sum">
          <Rich text={fact.teaser} />
        </p>
      </header>
      <LessonBlocks blocks={fact.blocks} />
      {next !== fact ? (
        <footer className="lesson-foot">
          <button type="button" className="next-lesson" onClick={() => go(factRoute(next))}>
            <span className="nl-k">Sıradaki bilgi</span>
            <span className="nl-t">
              <Rich text={next.title} inert />
            </span>
            <Icon name="right" />
          </button>
        </footer>
      ) : null}
    </article>
  );
}

export function FactsSection() {
  const [id] = useSubPath();
  const fact = id ? factById(id) : undefined;
  return fact ? <FactPage fact={fact} /> : <FactList />;
}
